import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { GraphError } from '@microsoft/microsoft-graph-client';
import { BehaviorSubject, NEVER, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppEnvironment, SapEnvironment, WorkCenters } from 'src/app/models';
import { IUser } from 'src/app/models/user';
import { SharePointService } from 'src/app/services/share-point.service'; // fixes circular import
import { environment } from 'src/environments/environment';
import { handleError } from './akita-error-helper';
import {
  ISessionPersist,
  readPersistedSession,
  writePersistedSession,
} from './session-persistence';
import { ValueState } from './value-state';

interface ISessionValue {
  persist: ISessionPersist;
  priority?: Map<string, string>;
  user: IUser;
  viewType?: Map<string, WorkCenters[]>;
}

function emptyUser(): IUser {
  return {
    displayName: undefined,
    email: undefined,
    firstName: undefined,
    id: undefined,
    imageSrc: undefined,
    jobTitle: undefined,
    lastName: undefined,
    shortName: undefined,
  };
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly state = new ValueState<ISessionValue>({
    persist: readPersistedSession(),
    user: emptyUser(),
  });

  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;
  public readonly user$ = this.state.selectValue(({ user }) => user);
  public readonly userName$ = this.state.selectValue(({ user }) => user.displayName);
  public readonly userShortName$ = this.state.selectValue(({ user }) => user.shortName);
  public readonly userImageSrc$ = this.state.selectValue(({ user }) => user.imageSrc);
  public readonly userJobTitle$ = this.state.selectValue(({ user }) => user.jobTitle);
  public readonly role$ = this.state.selectValue(({ persist }) => persist.currentRole);
  public readonly priority$ = this.state.selectValue(({ priority }) => priority);
  public readonly viewType$ = this.state.selectValue(({ viewType }) => viewType);

  public readonly sideMenuIsCollapsed$ = this.state.selectValue(
    ({ persist }) => persist.ui.sideMenu.isCollapsed
  );

  private profileLookup$?: Observable<void>;
  private readonly accountActive$ = new BehaviorSubject<boolean>(false);
  private readonly dataUrlPattern =
    /^data:(?:image\/(?!bmp|gif|jpeg|jpg|png|tiff|webp).*);base64,[a-z0-9+\/]+=*$/iu;

  public constructor(
    private readonly sharePointService: SharePointService, // TODO right name and place for this?
    private readonly domSanitizer: DomSanitizer,
    @Inject(MSAL_INSTANCE) private readonly msalInstance: IPublicClientApplication
  ) {}

  public isLoggedIn(): boolean {
    return !!this.state.value.user.shortName;
  }

  public hasRole(): boolean {
    return !!this.state.value.persist.currentRole;
  }

  public hasPriority(): boolean {
    return !!this.state.value.priority;
  }

  public hasViewType(): boolean {
    return !!this.state.value.viewType;
  }

  public isDevOrLocal() {
    return (
      environment.environmentName === AppEnvironment.CANARY ||
      environment.environmentName === AppEnvironment.LOCAL
    );
  }

  public getSelectedSapEnvironment() {
    return this.state.value.persist.ui.selectedSapEnvironment;
  }

  public loadCurrentUserProfile(): Observable<void> {
    if (!this.profileLookup$) {
      this.state.setLoading(true);

      this.profileLookup$ = this.sharePointService.getUserProfile().pipe(
        tap({
          next: (user) => {
            this.updateUser(user);
          },
          error: (error: Error | GraphError) => {
            const message = error.message
              ? `${error.message}${
                  error instanceof GraphError && error.code ? ` (code: ${error.code})` : ''
                }`
              : 'No reason was given 😭';

            this.state.setError(message);
            this.clearUser();
          },
        }),
        switchMap((user) => {
          return of(user);
        }),
        map(() => undefined),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
    }

    return this.profileLookup$;
  }

  public loadADUser(): Observable<void> {
    this.state.setLoading(true);

    return this.sharePointService.getUser().pipe(
      tap((user) => {
        const imageString = user.imageSrc;

        if (imageString && this.dataUrlPattern.test(imageString)) {
          // angular only trust bmp|gif|jpeg|jpg|png|tiff|webp
          // so we need to trust other formats like image/pjpeg
          // see https://github.com/angular/angular/issues/18950
          user.imageSrc =
            // cast to string because [nzSrc] doesn't accept SafeResourceUrl https://github.com/angular/angular/issues/33028
            this.domSanitizer.bypassSecurityTrustResourceUrl(imageString) as string;
        }

        this.updateUser(user);
      }),
      catchError((error) => {
        handleError(this.state, error);
        this.clearUser();

        return NEVER;
      }),
      map(() => undefined)
    );
  }

  public updateUser(user: IUser): void {
    this.state.update({ user });
    this.state.setLoading(false);
  }

  public clearUser(): void {
    this.state.update({ user: emptyUser() });
  }

  public updatePriority(priority: Map<string, string>): void {
    this.state.update({ priority });
  }

  public clearPriority(): void {
    this.state.update({ priority: undefined });
  }

  public updateViewType(viewType: Map<string, WorkCenters[]>): void {
    this.state.update({ viewType });
  }

  public setCurrentRole(role: string): void {
    this.updatePersist((persist) => ({ ...persist, currentRole: role }));
  }

  public toggleSideMenuIsCollapsed(): void {
    this.updatePersist((persist) => ({
      ...persist,
      ui: {
        ...persist.ui,
        sideMenu: { ...persist.ui.sideMenu, isCollapsed: !persist.ui.sideMenu.isCollapsed },
      },
    }));
  }

  public setActiveAccount(): void {
    const accounts = this.msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      this.msalInstance.setActiveAccount(accounts[0]);
      this.accountActive$.next(true);
    }
  }

  public setSelectedSapEnvironment(sapEnvironment: SapEnvironment) {
    this.updatePersist((persist) => ({
      ...persist,
      ui: { ...persist.ui, selectedSapEnvironment: sapEnvironment },
    }));

    window.location.reload(); // Reload page when selected environment is changed
  }

  /**
   * The single write path for the persisted subtree, so saving cannot be forgotten
   * by a future writer — this replaces what `persistState` did by subscription.
   */
  private updatePersist(project: (persist: ISessionPersist) => ISessionPersist): void {
    const persist = project(this.state.value.persist);

    this.state.update({ persist });
    writePersistedSession(persist);
  }
}
